import React, { Suspense, useEffect } from 'react';
import lazyComRecarga from './utils/lazyComRecarga';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PrivateRoute from './components/PrivateRoute';
import { iniciarMonitorDeSessao } from './services/monitorSessao';
import 'bootstrap/dist/css/bootstrap.min.css';
import './css/carregamento.css';

// Cada página vira um pacote próprio, baixado só quando é aberta. Antes tudo
// (calendário, gráficos, PDF...) vinha num JS único de ~2 MB, sem compressão.
// Ao falhar o carregamento (aba aberta antes de uma atualização), recarrega
// uma vez: ver utils/lazyComRecarga.
const LoginPage = lazyComRecarga(() => import('./pages/LoginPage'));
const ConfirmarEmail = lazyComRecarga(() => import('./pages/ConfirmarEmail'));
const DashboardPage = lazyComRecarga(() => import('./pages/DashboardPage'));
const ReportsPage = lazyComRecarga(() => import('./pages/ReportsPage'));
const BookingList = lazyComRecarga(() => import('./pages/AgendamentoPage'));
const AdminDashboard = lazyComRecarga(() => import('./pages/AdminDashboard'));
const CoordenadorDashboard = lazyComRecarga(() => import('./pages/CoordenadorDashboard'));
const NotFound = lazyComRecarga(() => import('./pages/NotFound'));
const EmDesenvolvimento = lazyComRecarga(() => import('./pages/EmDesenvolvimento'));
const HomePage = lazyComRecarga(() => import('./pages/HomePage'));
const HabilitacaoPage = lazyComRecarga(() => import('./pages/HabilitacoesPage'));
const ResponsabilidadesLabPage = lazyComRecarga(() => import('./pages/ResponsabilidadesLabPage'));
const MeuPerfil = lazyComRecarga(() => import('./pages/MeuPerfil'));
const EsqueciSenha = lazyComRecarga(() => import('./pages/EsqueciSenha'));
const RedefinirSenha = lazyComRecarga(() => import('./pages/RedefinirSenha'));
const NotificacoesPage = lazyComRecarga(() => import('./pages/NotificacoesPage'));
const VeiculosPage = lazyComRecarga(() => import('./pages/VeiculosPage'));
const DepartamentosPage = lazyComRecarga(() => import('./pages/DepartamentosPage'));
const AuditoriaPage = lazyComRecarga(() => import('./pages/AuditoriaPage'));
const TutorialPage = lazyComRecarga(() => import('./pages/TutorialPage'));

// Nome da aba por página: com várias abas abertas, todas eram "Sis Agenda - URCA".
const TITULOS = {
  '/': 'Início',
  '/login': 'Entrar',
  '/tutorial': 'Tutorial e ajuda',
  '/dashboard': 'Meu painel',
  '/coordenador': 'Painel da coordenação',
  '/admin': 'Painel administrativo',
  '/agendamentos': 'Solicitar agendamento',
  '/relatorios': 'Relatórios',
  '/meu-perfil': 'Meu perfil',
  '/veiculos': 'Veículos',
  '/departamentos': 'Departamentos',
  '/auditoria': 'Auditoria',
  '/habilitacoes': 'Habilitações',
  '/responsabilidades': 'Responsabilidades',
  '/notificacoes': 'Notificações',
  '/esqueci-senha': 'Esqueci a senha',
  '/redefinir-senha': 'Redefinir senha',
  '/confirmar-email': 'Confirmar e-mail',
};

const TituloDaPagina = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const titulo = TITULOS[pathname];
    document.title = titulo ? `${titulo} · SIS Agenda URCA` : 'SIS Agenda URCA';
  }, [pathname]);
  return null;
};

const CarregandoPagina = () => (
  <div className="rota-carregando" role="status" aria-live="polite">
    <span className="rota-carregando-anel" aria-hidden="true" />
    <span className="visually-hidden">Carregando…</span>
  </div>
);

function App() {

  // Renova o token de quem está usando e manda ao login quem voltou com ele vencido.
  useEffect(() => iniciarMonitorDeSessao(), []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      [...tooltipTriggerList].forEach(tooltipEl => {
        new window.bootstrap.Tooltip(tooltipEl);
      });
    }
  }, []);

  return (
    <div className='App'>
    <Router>
      <TituloDaPagina />
      <Suspense fallback={<CarregandoPagina />}>
      <Routes>

        <Route path="/" element={<HomePage />} />


        <Route path="/login" element={<LoginPage />} />

        <Route path="/tutorial" element={<TutorialPage />} />

        <Route path="/confirmar-email" element={<ConfirmarEmail />} />
        
        <Route path="/em-desenvolvimento" element={<EmDesenvolvimento />} />

		<Route path="/esqueci-senha" element={<EsqueciSenha />} />

        <Route path="/redefinir-senha" element={<RedefinirSenha/>} />


        <Route
          path="/dashboard"
          element={
            <PrivateRoute roles={['USUARIO']}>
              <DashboardPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/coordenador"
          element={
            <PrivateRoute roles={['COORDENADOR']}>
              <CoordenadorDashboard />
            </PrivateRoute>
          }
        />


        <Route
          path="/admin"
          element={
            <PrivateRoute roles={['ADMIN']}>
              <AdminDashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/agendamentos"
          element={
            <PrivateRoute>
              <BookingList />
            </PrivateRoute>
          }
        />


	<Route
	  path="/veiculos"
	  element={
		<PrivateRoute roles={['ADMIN', 'COORDENADOR']}>
		  <VeiculosPage />
		</PrivateRoute>
	  }
	/>

	<Route
	  path="/departamentos"
	  element={
		<PrivateRoute roles={['ADMIN', 'COORDENADOR']}>
		  <DepartamentosPage />
		</PrivateRoute>
	  }
	/>

	<Route
	  path="/auditoria"
	  element={
		<PrivateRoute roles={['ADMIN', 'COORDENADOR']}>
		  <AuditoriaPage />
		</PrivateRoute>
	  }
	/>


        <Route
          path="/relatorios"
          element={
            <PrivateRoute>
              <ReportsPage />
            </PrivateRoute>
          }
        />

        <Route 
            path="/meu-perfil" 
            element={
                <PrivateRoute>
                    <MeuPerfil />
                </PrivateRoute>
            } 
        />
		
		<Route
            path="/responsabilidades"
            element={
            <PrivateRoute roles={['ADMIN']}>
                <ResponsabilidadesLabPage />
            </PrivateRoute>
            }
        />

        <Route
            path="/habilitacoes"
            element={
            <PrivateRoute roles={['ADMIN', 'COORDENADOR']}>
                <HabilitacaoPage />
            </PrivateRoute>
            }
        />
		
		<Route
		  path="/notificacoes"
		  element={
			<PrivateRoute roles={['ADMIN']}>
			  <NotificacoesPage />
			</PrivateRoute>
		  }
		/>
            
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} />
    </Router>
    </div>
  );
}

export default App;